import React from 'react'
import type { MarketplaceBid } from '../../../services/backendService'
import '../styles/FarmBidsList.css'

interface FarmBidsListProps {
  bids: MarketplaceBid[]
  isWithdrawingBid?: boolean
  onWithdrawBid?: (bid: MarketplaceBid) => void
}

export const FarmBidsList: React.FC<FarmBidsListProps> = ({ bids, isWithdrawingBid = false, onWithdrawBid }) => {
  const [viewMode, setViewMode] = React.useState<'card' | 'list'>('card')

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return '#FF9800'
      case 'accepted':
        return '#4CAF50'
      case 'rejected':
        return '#f44336'
      case 'withdrawn':
        return '#9E9E9E'
      default:
        return '#757575'
    }
  }

  const groupedBids = {
    pending: bids.filter(b => b.status === 'pending'),
    accepted: bids.filter(b => b.status === 'accepted'),
    rejected: bids.filter(b => b.status === 'rejected'),
    withdrawn: bids.filter(b => b.status === 'withdrawn')
  }

  const getAvailabilityText = (bid: MarketplaceBid) => {
    if (!bid.expiresAt) {
      return 'Not specified'
    }

    const remainingMs = new Date(bid.expiresAt).getTime() - Date.now()
    const remainingDays = Math.max(0, Math.ceil(remainingMs / (1000 * 60 * 60 * 24)))
    return remainingDays === 0 ? 'Expires today' : `${remainingDays} day${remainingDays === 1 ? '' : 's'} left`
  }

  const renderBidCards = (title: string, sectionBids: MarketplaceBid[], cardClassName?: string) => {
    if (sectionBids.length === 0) {
      return null
    }

    return (
      <div className="bids-section">
        <h3>{title} ({sectionBids.length})</h3>
        <div className={`bids-grid ${viewMode !== 'card' ? 'bids-list-view' : ''}`}>
          {sectionBids.map(bid => (
            <div key={bid.id} className={`bid-card ${cardClassName || ''} ${viewMode !== 'card' ? 'list-view compact-view' : ''}`.trim()}>
              <div className="bid-header">
                <h4>{bid.jobTitle || bid.jobId}</h4>
                <span className="status" style={{ backgroundColor: getStatusColor(bid.status) }}>
                  {bid.status.toUpperCase()}
                </span>
              </div>
              <div className="bid-amount">${bid.bidAmount.toFixed(2)}</div>
              <div className="bid-details">
                <p><strong>Est. Days:</strong> {bid.estimatedDays}</p>
                <p><strong>Bid Validity:</strong> {getAvailabilityText(bid)}</p>
                <p><strong>Notes:</strong> {bid.notes || '—'}</p>
                <p><strong>Submitted:</strong> {new Date(bid.createdAt).toLocaleDateString()}</p>
              </div>
              {bid.status === 'pending' && onWithdrawBid && (
                <div className="bid-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    data-testid={`withdraw-bid-${bid.id}`}
                    disabled={isWithdrawingBid}
                    onClick={() => onWithdrawBid(bid)}
                  >
                    {isWithdrawingBid ? 'Cancelling...' : 'Cancel Bid'}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="farm-bids-list">
      {bids.length === 0 ? (
        <div className="empty-state">
          <p>No bids submitted yet. Start browsing available jobs!</p>
        </div>
      ) : (
        <>
          <div className="bids-view-toggle" aria-label="My bids view mode">
            <button
              type="button"
              className={`bids-view-toggle-btn ${viewMode === 'card' ? 'is-active' : ''}`}
              onClick={() => setViewMode('card')}
            >
              Cards
            </button>
            <button
              type="button"
              className={`bids-view-toggle-btn ${viewMode === 'list' ? 'is-active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              List
            </button>
          </div>
          {renderBidCards('Pending Bids', groupedBids.pending)}
          {renderBidCards('Accepted Bids', groupedBids.accepted, 'accepted')}
          {renderBidCards('Rejected Bids', groupedBids.rejected, 'rejected')}
          {renderBidCards('Withdrawn Bids', groupedBids.withdrawn)}
        </>
      )}
    </div>
  )
}

